import posthog, { type PostHog } from "posthog-js";
import { devWarn } from "../libs/dev-log";
import { scrubPublicProfileEvent } from "./posthog-scrub";

// Define allowed event verbs
const ALLOWED_VERBS = [
  "click",
  "submit",
  "create",
  "view",
  "add",
  "invite",
  "update",
  "delete",
  "accept",
  "reject",
  "remove",
  "start",
  "end",
  "cancel",
  "fail",
  "generate",
  "verify",
  "send",
  "share",
  "close",
  "reopen",
  "upload",
  "confirm",
] as const;

export type AllowedVerb = (typeof ALLOWED_VERBS)[number];

// Define event name format
export type EventName = `${AllowedVerb}_${string}` | "$pageview";

// Define user properties type
export type UserProperties = Record<string, string | number | boolean | null>;

/**
 * Define event properties type.
 *
 * Arrays of strings are allowed because PostHog stores and filters them
 * natively (`property includes 'x'`). The alternative — joining into one string
 * — turns an exact membership filter into a substring match, which is wrong the
 * first time one value is a prefix of another.
 */
export type EventProperties = Record<
  string,
  string | number | boolean | null | readonly string[]
>;

/** Persistence PostHog uses when capture is allowed vs. denied. "memory" writes
 * nothing to disk (no `ph_*` cookies) — the pre-consent default. */
export type ConsentPersistence = "localStorage+cookie" | "memory";

export type CaptureDecision = {
  capture: boolean;
  optOutByDefault: boolean;
  persistence: ConsentPersistence;
};

/**
 * Pure gating decision. Non-essential capture is allowed only when the env
 * kill-switch is on AND the user accepted analytics; otherwise PostHog stays
 * opted-out with in-memory persistence so nothing is written before consent
 * (SPEC AC 2, effective rule `env AND consent`).
 */
export function resolveCaptureState(
  envEnabled: boolean,
  consentAnalytics: boolean,
): CaptureDecision {
  const capture = envEnabled && consentAnalytics;
  return {
    capture,
    optOutByDefault: !capture,
    persistence: capture ? "localStorage+cookie" : "memory",
  };
}

/**
 * PostHogClient - Singleton class to ensure PostHog is initialized only once
 */
class PostHogClient {
  private static instance: PostHogClient;
  private initialized = false;
  private isEnabled = import.meta.env.VITE_POSTHOG_ENABLED === `true`;

  private instanceId: string | undefined;

  private constructor() {
    // Private constructor to prevent direct construction calls
  }

  /**
   * Get the singleton instance of PostHogClient
   */
  public static getInstance(): PostHogClient {
    if (!PostHogClient.instance) {
      PostHogClient.instance = new PostHogClient();
    }
    return PostHogClient.instance;
  }

  public getInstanceId(): string {
    if (!this.instanceId) {
      throw new Error("PostHog not initialized. Call init() first.");
    }
    return this.instanceId;
  }

  /**
   * Initialize PostHog analytics.
   *
   * @param consentAnalytics - whether the user has accepted analytics. PostHog is
   * initialized already in the correct state: capture only when `env AND consent`,
   * otherwise `opt_out_capturing_by_default` + `persistence: "memory"` so no `ph_*`
   * cookie is written and no event is sent before consent (SPEC AC 1-2, 10).
   */
  public init(consentAnalytics = false): void {
    if (this.initialized) {
      console.warn("PostHog already initialized");
      return;
    }

    const proxyDomain = import.meta.env
      .VITE_PUBLIC_REVERSE_PROXY_POSTHOG_DOMAIN;

    const decision = resolveCaptureState(this.isEnabled, consentAnalytics);

    posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
      api_host: proxyDomain,
      ui_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
      capture_pageview: false,
      capture_exceptions: false,
      opt_out_capturing_by_default: decision.optOutByDefault,
      persistence: decision.persistence,
      // Gli eventi della pagina pubblica non portano dati del visitatore
      // (AC16): la scrematura sta qui perché referrer, user agent e id
      // pubblicitari (`fbclid` & co., super-property di campagna) li allega
      // posthog-js, non il call site. Il cablaggio è pinnato in
      // `posthog-consent.test.ts`.
      before_send: scrubPublicProfileEvent,
      loaded: (ph) => {
        if (decision.capture) {
          this.applyEnabledConfig(ph as PostHog);
        } else {
          this.applyDisabledConfig(ph as PostHog);
        }
      },
    }) as PostHog | undefined;

    this.initialized = true;
    this.instanceId = posthog.get_distinct_id();
  }

  /**
   * Applies the full capturing config (persistent cookies + all capture surfaces
   * on, feature flags enabled). Shared by `init` (enabled branch) and
   * `enableCapture` (runtime opt-in).
   */
  private applyEnabledConfig(ph: PostHog): void {
    ph.opt_in_capturing();
    if ("set_config" in ph) {
      ph.set_config({
        persistence: "localStorage+cookie",
        capture_pageview: false,
        capture_pageleave: true,
        capture_heatmaps: true,
        capture_performance: true,
        capture_exceptions: true,
        autocapture: true,
        disable_session_recording: false,
        disable_surveys: false,
        advanced_disable_feature_flags: false,
      });
    }
  }

  /**
   * Applies the fully-disabled config (in-memory persistence, every capture
   * surface off, feature flags/session recording/surveys/exceptions off). This is
   * the pre-consent default and the post-revoke state. Shared by `init` (disabled
   * branch) and `disableCapture` (runtime opt-out).
   */
  private applyDisabledConfig(ph: PostHog): void {
    ph.opt_out_capturing();
    if ("set_config" in ph) {
      ph.set_config({
        persistence: "memory",
        capture_pageview: false,
        capture_pageleave: false,
        capture_heatmaps: false,
        capture_performance: false,
        capture_exceptions: false,
        autocapture: false,
        disable_session_recording: true,
        disable_surveys: true,
        advanced_disable_feature_flags: true,
      });
    }
  }

  /**
   * Runtime opt-in after the user accepts analytics — no page reload. Restores the
   * full capture config (persistent cookies + every capture surface on) so tracking
   * resumes immediately (SPEC AC 5, 11). No-throw: an adblocker or uninitialized
   * client fails silently; the choice stays persisted and capture resumes at the
   * next boot.
   */
  public enableCapture(): void {
    try {
      if (!this.initialized) return;
      this.applyEnabledConfig(posthog);
    } catch (error) {
      console.warn("PostHog enableCapture failed", error);
    }
  }

  /**
   * Runtime opt-out after the user refuses or revokes analytics — no reload.
   * Switches persistence back to memory, disables the full capture set, and
   * best-effort clears `ph_*` cookies. No-throw; revocation stays effective via
   * opt-out even if cookie cleanup partially fails (SPEC AC 6, 8, 10).
   */
  public disableCapture(): void {
    try {
      if (!this.initialized) return;
      this.applyDisabledConfig(posthog);
      this.clearPostHogCookies();
    } catch (error) {
      console.warn("PostHog disableCapture failed", error);
    }
  }

  /** Best-effort removal of `ph_*` cookies on revoke. Never throws. */
  private clearPostHogCookies(): void {
    try {
      if (typeof document === "undefined") return;
      const cookies = document.cookie ? document.cookie.split(";") : [];
      for (const cookie of cookies) {
        const name = cookie.split("=")[0]?.trim();
        if (name?.startsWith("ph_")) {
          // biome-ignore lint/suspicious/noDocumentCookie: best-effort ph_* removal on consent revoke; posthog-js exposes no API for it
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        }
      }
    } catch {
      // best-effort: revocation stays effective via opt-out even if this fails
    }
  }

  /**
   * Provides access to the underlying posthog instance
   */
  public getClient() {
    if (!this.initialized) {
      throw new Error("PostHog not initialized. Call init() first.");
    }
    return posthog;
  }

  public isIdentified(id: string) {
    return posthog.get_distinct_id() === id;
  }

  /**
   * Get the initialized state of PostHog
   */
  public getInitialized() {
    return this.initialized;
  }

  public getIsEnabled() {
    return this.isEnabled;
  }

  /**
   * Identify a user in PostHog
   * @param userId - The unique identifier for the user
   * @param properties - Optional user properties to set
   */
  public identify(userId: string, properties?: UserProperties): void {
    if (!this.initialized) {
      devWarn("Identify: PostHog not initialized. Call init() first.");
      if (!this.isEnabled) {
        devWarn("Identify: PostHog is disabled.");
      }
      return;
    }
    posthog.identify(userId, properties);
  }

  public reset(): void {
    posthog.reset();
  }

  /**
   * Capture an event in PostHog
   * @param eventName - The name of the event in format "verb_noun" (e.g., "click_button", "create_post")
   * @param properties - Optional properties to include with the event
   */
  public capture(eventName: EventName, properties?: EventProperties): void {
    if (!this.initialized) {
      devWarn("Capture: PostHog not initialized. Call init() first.");
      if (!this.isEnabled) {
        devWarn("Capture: PostHog is disabled.");
      }
      return;
    }

    if (eventName === "$pageview") {
      posthog.capture(eventName, properties);
      return;
    }

    // Validate event name format
    const [verb, ...rest] = eventName.split("_");
    if (!verb || !rest.length) {
      console.error(
        `Invalid event name format: ${eventName}. Must be in format "verb_noun"`,
      );
      return;
    }

    if (!ALLOWED_VERBS.includes(verb as AllowedVerb)) {
      console.error(
        `Invalid verb: ${verb}. Must be one of: ${ALLOWED_VERBS.join(", ")}`,
      );
      return;
    }

    posthog.capture(eventName, properties);
  }

  public captureException(err: Error): void {
    if (!this.initialized) {
      devWarn("CaptureException: PostHog not initialized. Call init() first.");
      return;
    }
    posthog.captureException(err);
  }
}

// Create the singleton instance
const postHogClient = PostHogClient.getInstance();

// Export the singleton instance
export default postHogClient;
